
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Container from '../ui/Container';
import Section from '../ui/Section';

export default function Services() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          // Reset animation when leaving the section
          setIsVisible(false);
        }
      },
      {
        threshold: 0.1, // Reduced threshold for mobile
        rootMargin: '0px 0px -50px 0px' // Reduced margin for mobile
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      title: t('services.website.title'),
      description: t('services.website.description'),
      benefits: t('services.website.benefits'),
      icon: 'ri-computer-line',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: t('services.landing.title'),
      description: t('services.landing.description'),
      benefits: t('services.landing.benefits'),
      icon: 'ri-pages-line',
      gradient: 'from-blue-600 to-blue-700'
    },
    {
      title: t('services.webapp.title'),
      description: t('services.webapp.description'),
      benefits: t('services.webapp.benefits'),
      icon: 'ri-smartphone-line',
      gradient: 'from-blue-700 to-blue-800'
    },
    {
      title: t('services.api.title'),
      description: t('services.api.description'),
      benefits: t('services.api.benefits'),
      icon: 'ri-settings-3-line',
      gradient: 'from-blue-800 to-blue-900'
    }
  ];

  return (
    <Section ref={sectionRef} id="services" className="py-20 bg-gradient-to-b from-white to-blue-50">
      <Container>
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4 font-poppins">
            {t('services.title')}
          </h2>
          <p className="text-xl text-blue-700 max-w-3xl mx-auto font-inter">
            {t('services.subtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div key={service.title} style={{
              transitionDelay: isVisible ? `${index * 200}ms` : '0ms' // Reduced delay for mobile
            }}>
            <Card
              key={service.title}
                className={`text-center group h-full flex flex-col transition-all duration-[1500ms] ease-out ${isVisible
                  ? 'opacity-100 translate-x-0 scale-100' 
                  : `opacity-0 scale-95 ${index < 2 ? '-translate-x-20' : 'translate-x-20'}`
              }`}

            >
              <div
                className={`w-16 h-16 bg-gradient-to-r ${service.gradient} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300`}
              >
                <i className={`${service.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-semibold text-blue-900 mb-4 font-poppins group-hover:text-blue-700 transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-blue-700 mb-4 font-inter leading-relaxed flex-grow">
                {service.description}
              </p>
              <p className="text-sm text-blue-600 mb-6 font-inter font-medium bg-blue-50 p-3 rounded-lg">
                {service.benefits}
              </p>
              <div className="mt-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scrollToContact}
                  className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 whitespace-nowrap"
                >
                  {t('services.getQuote')}
                </Button>
              </div>
            </Card>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}
